// Implementing a request queue in Axios interceptors
import axios, {AxiosRequestConfig} from 'axios'
import {SignOutService} from './auth.service/sign-out'
import Swal from 'sweetalert2'
import {DEFAULT_ERROR_MODAL} from '../constants/errors/error-model'
import {DEFAULT_CORMFIRM_MODAL} from '../constants/swal'
import {API_PATH} from '../constants/api-path'
import {IApiResponse} from '../interface/api-response/api-response'
import {ExceptionError} from './exception'
import DOMPurify from 'dompurify'

const client = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_BASE_URL || 'http://119.59.114.71:4002/api',
})
client.interceptors.request.use((request) => {
  const accessToken = localStorage.getItem('accessToken')
  if (!accessToken) return request

  request.headers!['Authorization'] = `Bearer ${accessToken}`
  request.headers!['Origin'] = `${process.env.REACT_APP_BACKEND_BASE_URL?.replace('/api', '')}`

  return request
})

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void
  reject: (error?: any) => void
  config: AxiosRequestConfig
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = []

// Flag to prevent multiple token refresh requests
let isRefreshing = false

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config

    console.log('error', error)
    if (error.response && error.response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true
        try {
          // Refresh the access token
          const newAccessToken = await refreshToken()

          // Update the request headers with the new access token
          error.config.headers['Authorization'] = `Bearer ${newAccessToken}`

          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({config, resolve, reject}) => {
            client
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err))
          })

          // Clear the queue
          refreshAndRetryQueue.length = 0

          // Retry the original request
          return client(originalRequest)
        } catch (refreshError: any) {
          // Handle token refresh error
          // You can clear all storage and redirect the user to the login page
          console.log('refreshError', refreshError)

          const signOutService = new SignOutService()
          await signOutService.request()
          // localStorage.removeItem('accessToken')
          // localStorage.removeItem('refreshToken')
          // localStorage.removeItem('kt-auth-react-v')
          // localStorage.removeItem('myCompany')
          localStorage.clear()
          sessionStorage.clear()
          await Swal.fire(DEFAULT_ERROR_MODAL(refreshError?.response?.data?.message))
          error.config.__isRetryRequest = false // Mark request for retry
          window.location.href = '/auth/login'
          throw refreshError
        } finally {
          isRefreshing = false
        }
      }

      // Add the original request to the queue
      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({config: originalRequest, resolve, reject})
      })
    } else if (error.response && error.response.status === 402) {
      const {value: text} = await Swal.fire({
        input: 'textarea',
        title: 'Activate License',
        inputPlaceholder: 'Type your license here...',
        inputAttributes: {
          'aria-label': 'Type your license here',
        },
        confirmButtonText: 'Submit',
        showCancelButton: true,
        allowOutsideClick: false,
      })
      if (text) {
        try {
          console.log('test', text)
          const sanitizedContent = DOMPurify.sanitize(text)
          console.log('sanitizedContent', sanitizedContent)
          await axios.post<IApiResponse<any>>(
            `${process.env.REACT_APP_BACKEND_BASE_URL}${API_PATH.LICENSE}/active-license`,
            {
              license: sanitizedContent,
            }
          )
          window.location.reload()
        } catch (error: any) {
          return Promise.reject(error)
        }
      } else {
        return Promise.reject(error)
      }
    } else {
      // Return a Promise rejection if the status code is not 401
      return Promise.reject(error)
    }
  }
)
const refreshToken = async () => {
  try {
    const {data: auth} = await axios.patch(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/authentication/refresh-token`,
      {
        accessToken: localStorage.getItem('accessToken'),
        refreshToken: localStorage.getItem('refreshToken'),
      }
    )

    if (auth) {
      localStorage.setItem('accessToken', auth.token)
      localStorage.setItem('refreshToken', auth.refreshToken)
      return auth.token
    }
  } catch (error) {
    throw error
  }
}

export default client
